/**
 * Aparência dos componentes do Connect (T4.4, plano seção 2.6). A API do
 * Connect só aceita string (hex/rgb/hsl) nas variáveis — não dá para
 * reaproveitar as classes NativeWind aqui, então os valores são copiados dos
 * tokens do tema (`app/tailwind.config.js`): `colorPrimary` é
 * `palette.orange.DEFAULT`, `colorBackground`/`colorText` são
 * `neutral.white`/`neutral.black`, `colorDanger` é `error.600`. São os
 * mesmos valores que a T0.3 já validou no spike
 * (`app/components/dev/connect-spike-screen.tsx`) — calm-ui, seção 6: reusar antes
 * de inventar, nenhuma cor nova.
 *
 * Um único objeto, sem imports de React Native: o provider web
 * (`@stripe/connect-js`), o provider nativo (`@stripe/stripe-react-native`)
 * e a página HTML da WebView (`connect-webview-html.ts`) usam o mesmo
 * formato (`{ variables: {...} }`).
 */
export const CONNECT_APPEARANCE = {
  variables: {
    fontFamily: 'Poppins, system-ui, sans-serif',
    fontSizeBase: '15px',
    spacingUnit: '10px',
    colorPrimary: '#FD5001',
    // Fundo igual ao da página (`neutral.offWhite`): o componente some como
    // "card branco" e os campos brancos viram os cartões.
    colorBackground: '#F5F5F5',
    colorText: '#000000',
    colorSecondaryText: '#737373',
    colorBorder: '#E5E5E5',
    colorDanger: '#DC2626',
    borderRadius: '16px',
    buttonBorderRadius: '999px',
    formBorderRadius: '12px',
    badgeBorderRadius: '999px',
    buttonPrimaryColorBackground: '#FD5001',
    buttonPrimaryColorBorder: '#FD5001',
    buttonPrimaryColorText: '#FFFFFF',
    buttonSecondaryColorBackground: '#FFFFFF',
    buttonSecondaryColorBorder: '#E5E5E5',
    buttonSecondaryColorText: '#000000',
    formBackgroundColor: '#FFFFFF',
    formHighlightColorBorder: '#FD5001',
    actionPrimaryColorText: '#FD5001',
    headingXlFontWeight: '600',
    headingLgFontWeight: '600',
    headingMdFontWeight: '600',
  },
};

// Direto da paleta (não do hook) para manter este arquivo sem React Native.
// eslint-disable-next-line @typescript-eslint/no-require-imports
const { semantic, darkScales } = require('@/constants/palette') as {
  semantic: { dark: { background: string; foreground: string; card: string; border: string } };
  darkScales: Record<string, Record<string, string>>;
};
const DARK = { ...semantic.dark, neutral: darkScales.neutral, error: darkScales.error };

/**
 * Mesma aparência no tema escuro: os tons vêm de `constants/palette.js`
 * (fundo da página, card, neutros espelhados), o laranja não muda.
 */
export const CONNECT_APPEARANCE_DARK = {
  variables: {
    ...CONNECT_APPEARANCE.variables,
    colorBackground: DARK.background,
    colorText: DARK.foreground,
    colorSecondaryText: DARK.neutral['500'],
    colorBorder: DARK.border,
    colorDanger: DARK.error['600'],
    buttonSecondaryColorBackground: DARK.card,
    buttonSecondaryColorBorder: DARK.border,
    buttonSecondaryColorText: DARK.foreground,
    formBackgroundColor: DARK.card,
  },
};

export function connectAppearance(scheme: 'light' | 'dark') {
  return scheme === 'dark' ? CONNECT_APPEARANCE_DARK : CONNECT_APPEARANCE;
}

/** Poppins do app dentro do iframe do Connect (não herda fonte da página). */
export const CONNECT_FONTS = [
  {
    cssSrc:
      'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap',
  },
];

/** Locale único do app — sem seletor de idioma (plano, escopo da T4.4). */
export const CONNECT_LOCALE = 'pt-BR';
